import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type {
    ConfigVariable,
    Scope,
    SourceBrand,
    SourceModel,
    SourceOperator,
    OperatorModelsResponse,
    Supplier,
} from "@types"

// =====================
// Type definitions des réponses des services
// =====================

export type VariablesResponse = ConfigVariable[]
export type ScopeResponse = Scope
export type OperatorsResponse = SourceOperator[]
export type SourceBrandsResponse = SourceBrand[]
export type SourceModelsResponse = SourceModel[]
export type SuppliersResponse = Supplier[]

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer les appels API pour récupérer les données de configuration
 * (variables, opérateurs, marques, modèles, fournisseurs)
 */
export class ConfigService {
    /**
     * Récupère la liste des variables disponibles
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Liste des variables
     */
    async getVariables(
        abortController?: AbortController
    ): Promise<VariablesResponse> {
        return handleServiceCall<VariablesResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/variable`, {
                signal: abortController?.signal,
            })
            return data
        })
    }

    /**
     * Récupère la liste des opérateurs disponibles
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Liste des opérateurs
     */
    async getOperators(
        abortController?: AbortController
    ): Promise<OperatorsResponse> {
        return handleServiceCall<OperatorsResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/operator`, {
                signal: abortController?.signal,
            })
            return data
        })
    }

    /**
     * Récupère les modèles et marques disponibles pour un opérateur donné
     * @param operator - Identifiant de l'opérateur
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Modèles et marques de l'opérateur
     */
    async getOperatorModelsByOperator(
        operator: string,
        abortController?: AbortController
    ): Promise<OperatorModelsResponse> {
        return handleServiceCall<OperatorModelsResponse>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/operator/${operator}/models`,
                {
                    signal: abortController?.signal,
                }
            )
            return data
        })
    }

    /**
     * Récupère la liste des marques de sources disponibles
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Liste des marques de sources
     */
    async getSourceBrands(
        abortController?: AbortController
    ): Promise<SourceBrandsResponse> {
        return handleServiceCall<SourceBrandsResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/sourcebrand`, {
                signal: abortController?.signal,
            })
            return data
        })
    }

    /**
     * Récupère la liste des modèles de sources disponibles
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Liste des modèles de sources
     */
    async getSourceModels(
        abortController?: AbortController
    ): Promise<SourceModelsResponse> {
        return handleServiceCall<SourceModelsResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/sourcemodel`, {
                signal: abortController?.signal,
            })
            return data
        })
    }

    /**
     * Récupère le scope courant (marque / tenant)
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Le scope
     */
    async getScope(abortController?: AbortController): Promise<ScopeResponse> {
        return handleServiceCall<ScopeResponse>(async () => {
            const scopeRefererDev = import.meta.env.DEV
                ? import.meta.env.VITE_SCOPE_REFERER_DEV
                : undefined

            const headers = {} as Record<string, string>

            if (scopeRefererDev) {
                headers["x-scope-referer"] = scopeRefererDev
            }

            const { data } = await axios.get(`/api/metier/v5/scope`, {
                signal: abortController?.signal,
                headers,
            })
            return data
        })
    }

    /**
     * Récupère la liste des fournisseurs d'énergie disponibles
     * @param abortController - Contrôleur d'annulation optionnel
     * @returns Liste des fournisseurs d'énergie
     */
    async getSuppliers(
        abortController?: AbortController
    ): Promise<SuppliersResponse> {
        return handleServiceCall<SuppliersResponse>(async () => {
            const {
                data: { data },
            } = await axios.get(`/api/metier/energysupplier`, {
                signal: abortController?.signal,
            })
            return data
        })
    }
}
